import { mountGisButton } from './sso-gis-adapter';
import { signInWithOidc } from './sso-oidc-adapter';
import { SsoNotConfiguredError } from './sso-errors';
import { SsoAdapterKind, SsoIdToken, SsoProviderId } from './sso-models';
import { getSsoProviderConfig } from './sso-config';

/**
 * Whether this provider renders its own button. Only Google does, and only
 * because its browser flow has no imperative entry point.
 */
export function getSsoAdapterKind(provider: SsoProviderId): SsoAdapterKind {
    const config = getSsoProviderConfig(provider);

    if (!config) {
        throw new SsoNotConfiguredError();
    }

    return config.adapter === 'gis' ? 'rendered' : 'imperative';
}

/** Starts the flow for a provider we drive from our own button. */
export function signInWithSso(provider: SsoProviderId): Promise<SsoIdToken> {
    const config = getSsoProviderConfig(provider);

    if (!config) {
        return Promise.reject(new SsoNotConfiguredError());
    }

    if (config.adapter === 'gis') {
        return Promise.reject(new Error('SSO_WRONG_ADAPTER'));
    }

    return signInWithOidc(config);
}

export type MountSsoButtonOptions = {
    width: number;
    onIdToken: (token: SsoIdToken) => void;
    onError: (error: unknown) => void;
};

/**
 * Renders a provider's own button into `parent`. Returns a cleanup function.
 */
export function mountSsoButton(
    provider: SsoProviderId,
    parent: HTMLElement,
    options: MountSsoButtonOptions
): () => void {
    const config = getSsoProviderConfig(provider);

    if (!config || config.adapter !== 'gis') {
        options.onError(new SsoNotConfiguredError());
        return () => undefined;
    }

    return mountGisButton(config, parent, options);
}
